import { lteRequest } from "./client";

/** Aggregate LTE (Go domain.ExecutionTask + store.TaskRecord). Pas de titre côté
 * backend : l'affichage se compose de execution_type + zone_ref/site_ref. */
export interface LteTask {
  id: string;
  tenant_id: string;
  version: number;
  status:
    | "planned"
    | "assigned"
    | "accepted"
    | "started"
    | "paused"
    | "blocked"
    | "completed"
    | "cancelled"
    | "rejected";
  execution_type:
    "standard" | "emergency" | "maintenance" | "inspection" | "installation";
  priority: "low" | "medium" | "high" | "critical";
  due_end?: string;
  site_ref?: string;
  zone_ref?: string;
  context_refs: string[];
  pm_work_item_id?: string;
  template_id?: string;
  required_proofs?: string[];
  created_by: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface SupervisorTask extends LteTask {
  overdue: boolean;
}

export interface LteList<T> {
  items: T[];
  next_cursor?: string;
}

/** Statuts terminaux LTE — une tâche close ne compte plus dans les listes/compteurs « ouverts ». */
export const LTE_CLOSED_STATUSES: string[] = [
  "completed",
  "cancelled",
  "rejected",
];

export type LteCommand =
  | "accept"
  | "start"
  | "pause"
  | "resume"
  | "block"
  | "complete"
  | "cancel"
  | "reject";

export interface CreateLteTaskInput {
  execution_type?: LteTask["execution_type"];
  priority?: LteTask["priority"];
  due_end?: string;
  site_ref?: string;
  zone_ref?: string;
  context_refs?: string[];
  pm_work_item_id?: string;
  template_id?: string;
}

export function listLteTasks(cursor?: string): Promise<LteList<LteTask>> {
  // limit = max backend (200) au lieu du défaut 50 ; les appelants doivent
  // suivre next_cursor (cf. fetchAllPages) pour éviter la troncature silencieuse.
  return lteRequest({
    method: "GET",
    url: "/tasks",
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function getLteTask(id: string): Promise<LteTask> {
  return lteRequest({ method: "GET", url: `/tasks/${id}` });
}

export function createLteTask(body: CreateLteTaskInput): Promise<LteTask> {
  return lteRequest({ method: "POST", url: "/tasks", data: body });
}

export function sendLteCommand(
  id: string,
  command: LteCommand,
  version: number,
  reason?: string,
): Promise<LteTask> {
  return lteRequest({
    method: "POST",
    url: `/tasks/${id}/${command}`,
    data: { version, ...(reason ? { reason } : {}) },
  });
}

export function submitLteProof(
  id: string,
  body: { proof_type: string; proof_uri?: string; payload?: unknown },
): Promise<unknown> {
  return lteRequest({ method: "POST", url: `/tasks/${id}/proofs`, data: body });
}

export function declareLteException(
  id: string,
  body: { kind: string; severity: string },
): Promise<unknown> {
  return lteRequest({
    method: "POST",
    url: `/tasks/${id}/exceptions`,
    data: body,
  });
}

export function assignLteTask(
  id: string,
  body: { target_type: string; role: string; target_ref: string },
): Promise<unknown> {
  return lteRequest({ method: "POST", url: `/tasks/${id}/assign`, data: body });
}

/** Ligne execution_assignments (Go domain.ExecutionAssignment). target_ref
 * porte le worker_url quand target_type = "worker". */
export interface LteAssignment {
  id: string;
  tenant_id: string;
  task_id: string;
  target_ref?: string;
  target_type: "worker" | "team";
  role: "driver" | "contributor" | "supervisor";
  status: "pending" | "accepted" | "declined" | "active" | "cancelled";
  created_at: string;
  updated_at: string;
}

/** Statuts qui « occupent » le rôle — set exact de l'index unique partiel
 * uq_execution_assignments_one_driver (migration 002). declined/cancelled sont
 * terminaux et libèrent le slot : une tâche déclinée n'a plus de titulaire. */
export const LTE_ACTIVE_ASSIGNMENT_STATUSES: string[] = [
  "pending",
  "accepted",
  "active",
];

export function listLteAssignments(
  taskId: string,
): Promise<LteList<LteAssignment>> {
  return lteRequest({ method: "GET", url: `/tasks/${taskId}/assignments` });
}

export function listSupervisorOpen(): Promise<LteList<SupervisorTask>> {
  return lteRequest({ method: "GET", url: "/supervisor/open" });
}
